import { useEffect, useRef } from 'react'

// From https://developer.mozilla.org/en-US/docs/Web/CSS/background-color
const bodyBackgroundColorInitialVal = 'transparent'

/**
 * Use to change the background color of document.body.
 *
 * Will clean itself to restore the original body background color
 * when the hook is unmounted
 *
 * Pass the color to change the body background to, or
 * `undefined` to not change the color.
 *
 * NOTE: https://styled-components.com/docs/api#createglobalstyle
 * is more appropriate for this functionality.
 *
 * This hook should be removed and functionality refactored when
 * styled-components is upgraded to support createGlobalStyle
 */
const useBodyBackgroundColor = (color: string | undefined) => {
  const origColorRef = useRef<string | undefined>(undefined)

  // On first invocation, store the current body color
  useEffect(() => {
    origColorRef.current = document.body.style.backgroundColor
  }, [])

  useEffect(() => {
    if (document && color) {
      document.body.style.backgroundColor = color
    }

    return () => {
      // Replace original body background color
      document.body.style.backgroundColor =
        origColorRef.current || bodyBackgroundColorInitialVal
    }
  }, [color])
}

export default useBodyBackgroundColor
